import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const links = [
  { label: "Home", href: "#hero" },
  { label: "About", href: "#about" },
  { label: "Legacy", href: "#legacy" },
  { label: "Royale Raas", href: "#royale-raas" },
  { label: "Gallery", href: "#gallery" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Anchor links (e.g. "#hero") only scroll natively when already on "/".
  // From any other route, do a client-side navigation to the home page
  // with the hash so Home.jsx can scroll to the right section once mounted.
  const handleAnchorClick = (e, hash) => {
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate(`/${hash}`);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-strong py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6">
        <a href="/" data-testid="nav-logo" className="flex items-center">
          <img
            src="/logo/elite-eventz-logo.png"
            alt="Elite Eventz"
            className="h-10 sm:h-11 w-auto select-none"
            draggable={false}
          />
        </a>

        <ul className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={location.pathname === "/" ? l.href : `/${l.href}`}
                onClick={(e) => handleAnchorClick(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative text-[0.92rem] text-neutral-300 hover:text-white transition group tracking-wide"
              >
                {l.label}
                <span className="absolute -bottom-2 left-0 h-px w-0 bg-[var(--ee-gold)] transition-all duration-500 group-hover:w-full" />
              </a>
            </li>
          ))}
          <li>
            <a
              href={location.pathname === "/" ? "#careers" : "/#careers"}
              onClick={(e) => handleAnchorClick(e, "#careers")}
              data-testid="nav-link-career-with-us"
              className="relative text-[0.92rem] text-neutral-300 hover:text-white transition group tracking-wide"
            >
              Career With Us
              <span className="absolute -bottom-2 left-0 h-px w-0 bg-[var(--ee-gold)] transition-all duration-500 group-hover:w-full" />
            </a>
          </li>
          <li>
            <a
              href={location.pathname === "/" ? "#contact" : "/#contact"}
              onClick={(e) => handleAnchorClick(e, "#contact")}
              data-testid="nav-link-contact"
              className="relative text-[0.92rem] text-neutral-300 hover:text-white transition group tracking-wide"
            >
              Contact
              <span className="absolute -bottom-2 left-0 h-px w-0 bg-[var(--ee-gold)] transition-all duration-500 group-hover:w-full" />
            </a>
          </li>
        </ul>

        <a
          href={location.pathname === "/" ? "#royale-raas" : "/#royale-raas"}
          onClick={(e) => handleAnchorClick(e, "#royale-raas")}
          data-testid="nav-cta-book"
          className="hidden md:inline-flex btn-gold text-sm py-2.5 px-5"
        >
          Book Tickets
        </a>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden glass-strong border-t border-[var(--ee-border)]"
        >
          <ul className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={location.pathname === "/" ? l.href : `/${l.href}`}
                  onClick={(e) => {
                    handleAnchorClick(e, l.href);
                    setOpen(false);
                  }}
                  className="block py-2 text-neutral-200"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <a
                href={location.pathname === "/" ? "#careers" : "/#careers"}
                onClick={(e) => {
                  handleAnchorClick(e, "#careers");
                  setOpen(false);
                }}
                className="block py-2 text-neutral-200"
              >
                Career With Us
              </a>
            </li>
            <li>
              <a
                href={location.pathname === "/" ? "#contact" : "/#contact"}
                onClick={(e) => {
                  handleAnchorClick(e, "#contact");
                  setOpen(false);
                }}
                className="block py-2 text-neutral-200"
              >
                Contact
              </a>
            </li>
            <li>
              <a
                href={location.pathname === "/" ? "#royale-raas" : "/#royale-raas"}
                onClick={(e) => {
                  handleAnchorClick(e, "#royale-raas");
                  setOpen(false);
                }}
                className="btn-gold w-full mt-2"
              >
                Book Tickets
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
